import { doodBlockConfig, propertyValue } from "./dood-block-config";
import { composeTabListConfig } from "./atoms/tab-list-atom.config";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";

type PreviewCatalogProduct = {
  id: string;
  _id: string;
  shop_id: string;
  name: string;
  description?: string;
  price: number;
  final_price: number;
  vat: string;
  additions_group: string;
  categories: readonly unknown[];
  additions: { products: readonly unknown[] };
  images?: {
    thumbnail?: { url: string; colors?: string[] };
  };
};

/**
 * Category node of the shop product-list preview.
 * `level` matches the dashboard catalog (0 = root … 2 = deepest, three visual levels).
 * A node may hold products and children at the same time.
 */
export type ShopProductListPreviewCategoryNode = {
  id: string;
  level: number;
  category: {
    id: string;
    name: string;
    description?: string;
    available: boolean;
    display_type: string;
    products: readonly string[];
  };
  products: readonly PreviewCatalogProduct[];
  children: readonly ShopProductListPreviewCategoryNode[];
};

/** Tab mirroring a preview category node, including nested levels. */
export type ShopProductListPreviewTreeTab = {
  id: string;
  name: string;
  icon?: string;
  level: number;
  children: readonly ShopProductListPreviewTreeTab[];
};

/**
 * Serializable preview VM for `ShopProductListBlockComponent`.
 * Kept in content-builder so dashboard + webapp share the same transport shape.
 */
export type ShopProductListBlockScenarioContent = {
  shop: {
    id: string;
    name: string;
    available: boolean;
    available_at?: string;
  } | null;
  isShopLoading: boolean;
  cartProducts: readonly unknown[];
  tagColors: {
    font: { type: string; value: string };
    background: { type: string; value: string };
  };
  filterColors: {
    banner: { type: string; value: string };
    inactiveColors: {
      font: { type: string; value: string };
      background: { type: string; value: string };
    };
    activeColors: {
      font: { type: string; value: string };
      background: { type: string; value: string };
    };
  };
  treeTabsOfCatalogCategory: readonly ShopProductListPreviewTreeTab[];
  selectedLabel?: undefined;
  categoriesWithProductsFilteredByLabel: readonly ShopProductListPreviewCategoryNode[];
  categoriesId: readonly string[];
  warningMessages?: readonly string[];
};

/**
 * Legacy CB1 field order for `ShopProductListBlock` (commit 58ae0676e).
 * Restores headings, groupedFields and leaf sequence for the dashboard editor.
 */
export const SHOP_PRODUCT_LIST_LEGACY_FIELD_ORDER = [
  { type: "heading", value: "General" },
  "background_color",
  "display_allergen_and_tag_filters",
  { type: "heading", value: "Category list" },
  "display_burger_menu_categories",
  "category_list_top",
  "burger_background_color",
  "burger_icon_color",
  "categories_background_color",
  "categories_active_color",
  "categories_inactive_color",
  "secondary_categories_colors",
  "third_categories_colors",
  { type: "heading", value: "Category names" },
  "category_name_color",
  "category_description_color",
  "highlight_category_name_color",
  "highlight_category_background_color",
  { type: "heading", value: "Product cards" },
  "product_name_color",
  "product_description_color",
  "product_price_color",
  "product_image_placeholder_color",
  "product_border_color",
  "product_background_color",
  {
    type: "groupedFields",
    handle: "product_hover_colors",
    extras: [
      {
        type: "messageUtil",
        options: {
          type: "info",
          message:
            "Customize the colors when hovering over a product card. Leave fields empty to use the default product colors",
        },
      },
    ],
    fields: ["backgroundColor", "nameColor", "descriptionColor", "priceColor"],
  },
  {
    type: "groupedFields",
    handle: "product_highlight_colors",
    extras: [
      {
        type: "messageUtil",
        options: {
          type: "info",
          message:
            "These colors are optional and will take the default colors from the products colors",
        },
      },
    ],
    fields: [
      "highlight_product_name_color",
      "highlight_product_description_color",
      "highlight_product_price_color",
      "highlight_product_image_placeholder_color",
      "highlight_product_border_color",
      "highlight_product_background_color",
    ],
  },
  { type: "heading", value: "Back button" },
  "display_back_shop_list_button_mobile",
  "display_back_shop_list_button_desktop",
  "back_shop_list_button_text",
  "shop_list_url",
  "shop_list_button_color",
  "shop_list_button_text_color",
] as const satisfies readonly DescriptorFieldOrderItem[];

/**
 * `ShopProductListBlock` declared through `doodBlockConfig`.
 *
 * Prefixed export: `composeShopProductListConfig` (derived from `ShopProductListBlockComponent`).
 */
const {
  shopProductListBlockDescriptor: _generatedShopProductListBlockDescriptor,
  shopProductListBlockBlocDefinition,
  composeShopProductListConfig,
  shopProductListBlockDashboardLayout,
  shopProductListBlockConfig,
  shopProductListBlockResolveConfig,
  shopProductListBlockProperties,
  shopProductListBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    id: "shop-page-product-list-block",
    name: "Shop - Product list",
    componentType: "ShopProductListBlockComponent",
    category: "catalog",
    description: "Product catalogue and category navigation for a shop page.",
    keywords: ["shop", "product", "catalog", "categories", "menu"],
    allowedIn: [
      { bloc: "ShopPageComponent", slot: "body" },
      { bloc: "ShopDigitalMenuPageComponent", slot: "body" },
    ],
    sharedFields: {},
  },

  () => ({
    background_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "transparent" },
      options: {
        label: "pages.blocks.shop-product-list.background-color.label",
      },
    }),
    display_allergen_and_tag_filters: propertyValue({
      type: "checkbox",
      value: false,
      options: {
        label:
          "pages.blocks.shop-product-list.display-allergen-and-tag-filters.label",
        help: "pages.blocks.shop-product-list.display-allergen-and-tag-filters.help",
      },
    }),
    category_list_top: propertyValue({
      type: "number",
      value: 0,
      options: {
        label: "pages.blocks.shop-product-list.category-list-top.label",
        help: "pages.blocks.shop-product-list.category-list-top.help",
      },
    }),
    ...composeTabListConfig
      .withAlias({
        properties: {
          display_burger_navigation: "display_burger_menu_categories",
        },
      })
      // Helpers typing after withAlias+withOmit is incomplete for nested color props.
      .withOmit(["padding_x"])((helpers: any) => ({
      display_burger_menu_categories: helpers.display_burger_menu_categories({
        value: true,
        options: {
          label:
            "pages.blocks.shop-product-list.display-burger-menu-categories.label",
        },
      }),
      burger_background_color: helpers.burger_background_color({
        options: {
          label: "pages.blocks.shop-product-list.burger-background-color.label",
        },
      }),
      burger_icon_color: helpers.burger_icon_color({
        options: {
          label: "pages.blocks.shop-product-list.burger-icon-color.label",
        },
      }),
      categories_background_color: helpers.categories_background_color({
        options: {
          label:
            "pages.blocks.shop-product-list.categories-background-color.label",
        },
      }),
      categories_active_color: helpers.categories_active_color({
        options: {
          label: "pages.blocks.shop-product-list.categories-active-color.label",
        },
      }),
      categories_inactive_color: helpers.categories_inactive_color({
        options: {
          label:
            "pages.blocks.shop-product-list.categories-inactive-color.label",
        },
      }),
      secondary_categories_colors: {
        backgroundColor: helpers.secondary_categories_colors.backgroundColor({
          options: {
            label:
              "pages.blocks.shop-product-list.secondary-categories-colors.background-color.label",
          },
        }),
        activeColor: helpers.secondary_categories_colors.activeColor({
          options: {
            label:
              "pages.blocks.shop-product-list.secondary-categories-colors.active-color.label",
          },
        }),
        inactiveColor: helpers.secondary_categories_colors.inactiveColor({
          options: {
            label:
              "pages.blocks.shop-product-list.secondary-categories-colors.inactive-color.label",
          },
        }),
      },
      third_categories_colors: {
        backgroundColor: helpers.third_categories_colors.backgroundColor({
          options: {
            label:
              "pages.blocks.shop-product-list.third-categories-colors.background-color.label",
          },
        }),
        activeColor: helpers.third_categories_colors.activeColor({
          options: {
            label:
              "pages.blocks.shop-product-list.third-categories-colors.active-color.label",
          },
        }),
        inactiveColor: helpers.third_categories_colors.inactiveColor({
          options: {
            label:
              "pages.blocks.shop-product-list.third-categories-colors.inactive-color.label",
          },
        }),
      },
    })),
    category_name_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-900" },
      options: {
        label: "pages.blocks.shop-product-list.category-name-color.label",
      },
    }),
    category_description_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-600" },
      options: {
        label:
          "pages.blocks.shop-product-list.category-description-color.label",
      },
    }),
    highlight_category_name_color: propertyValue({
      type: "color",
      value: { type: "default", value: "" },
      options: {
        label:
          "pages.blocks.shop-product-list.highlight-category-name-color.label",
        colorInheritFrom: "category_name_color",
      },
    }),
    highlight_category_background_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-600" },
      options: {
        label:
          "pages.blocks.shop-product-list.highlight-category-background-color.label",
      },
    }),
    product_name_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-900" },
      options: {
        label: "pages.blocks.shop-product-list.product-name-color.label",
      },
    }),
    product_description_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-900" },
      options: {
        label: "pages.blocks.shop-product-list.product-description-color.label",
      },
    }),
    product_price_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-900" },
      options: {
        label: "pages.blocks.shop-product-list.product-price-color.label",
      },
    }),
    product_image_placeholder_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-50" },
      options: {
        label:
          "pages.blocks.shop-product-list.product-image-placeholder-color.label",
      },
    }),
    product_border_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-100" },
      options: {
        label: "pages.blocks.shop-product-list.product-border-color.label",
      },
    }),
    product_background_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "transparent" },
      options: {
        label: "pages.blocks.shop-product-list.product-background-color.label",
      },
    }),
    product_hover_colors: {
      backgroundColor: propertyValue({
        type: "color",
        value: { type: "palette", value: "neutral-50" },
        options: {
          label:
            "pages.blocks.shop-product-list.product-hover-colors.background-color.label",
          help: "pages.blocks.shop-product-list.product-hover-colors.help",
        },
      }),
      nameColor: propertyValue({
        type: "color",
        value: { type: "default", value: "" },
        options: {
          label:
            "pages.blocks.shop-product-list.product-hover-colors.name-color.label",
          colorInheritFrom: "product_name_color",
        },
      }),
      descriptionColor: propertyValue({
        type: "color",
        value: { type: "default", value: "" },
        options: {
          label:
            "pages.blocks.shop-product-list.product-hover-colors.description-color.label",
          colorInheritFrom: "product_description_color",
        },
      }),
      priceColor: propertyValue({
        type: "color",
        value: { type: "default", value: "" },
        options: {
          label:
            "pages.blocks.shop-product-list.product-hover-colors.price-color.label",
          colorInheritFrom: "product_price_color",
        },
      }),
    },
    product_highlight_colors: {
      // Nested keys must be snake_case to match CB2 property keys / draft props.
      highlight_product_name_color: propertyValue({
        type: "color",
        value: { type: "default", value: "" },
        options: {
          label:
            "pages.blocks.shop-product-list.product-highlight-colors.highlight-product-name-color.label",
          help: "pages.blocks.shop-product-list.product-highlight-colors.help",
          colorInheritFrom: "product_name_color",
        },
      }),
      highlight_product_description_color: propertyValue({
        type: "color",
        value: { type: "default", value: "" },
        options: {
          label:
            "pages.blocks.shop-product-list.product-highlight-colors.highlight-product-description-color.label",
          colorInheritFrom: "product_description_color",
        },
      }),
      highlight_product_price_color: propertyValue({
        type: "color",
        value: { type: "default", value: "" },
        options: {
          label:
            "pages.blocks.shop-product-list.product-highlight-colors.highlight-product-price-color.label",
          colorInheritFrom: "product_price_color",
        },
      }),
      highlight_product_image_placeholder_color: propertyValue({
        type: "color",
        value: { type: "default", value: "" },
        options: {
          label:
            "pages.blocks.shop-product-list.product-highlight-colors.highlight-product-image-placeholder-color.label",
          colorInheritFrom: "product_image_placeholder_color",
        },
      }),
      highlight_product_border_color: propertyValue({
        type: "color",
        value: { type: "default", value: "" },
        options: {
          label:
            "pages.blocks.shop-product-list.product-highlight-colors.highlight-product-border-color.label",
          colorInheritFrom: "product_border_color",
        },
      }),
      highlight_product_background_color: propertyValue({
        type: "color",
        value: { type: "default", value: "" },
        options: {
          label:
            "pages.blocks.shop-product-list.product-highlight-colors.highlight-product-background-color.label",
          colorInheritFrom: "product_background_color",
        },
      }),
    },
    display_back_shop_list_button_mobile: propertyValue({
      type: "checkbox",
      value: false,
      options: {
        label:
          "pages.blocks.shop-product-list.display-back-shop-list-button-mobile.label",
      },
    }),
    display_back_shop_list_button_desktop: propertyValue({
      type: "checkbox",
      value: false,
      options: {
        label:
          "pages.blocks.shop-product-list.display-back-shop-list-button-desktop.label",
      },
    }),
    back_shop_list_button_text: propertyValue({
      type: "text",
      value: "",
      options: {
        label:
          "pages.blocks.shop-product-list.back-shop-list-button-text.label",
        help: "pages.blocks.shop-product-list.back-shop-list-button-text.help",
      },
    }),
    shop_list_url: propertyValue({
      type: "link",
      value: "",
      options: {
        label: "pages.blocks.shop-product-list.shop-list-url.label",
        help: "pages.blocks.shop-product-list.shop-list-url.help",
        defaultMode: "page",
        modes: ["page", "external"],
      },
    }),
    shop_list_button_color: propertyValue({
      type: "color",
      value: { type: "default", value: "" },
      options: {
        label: "pages.blocks.shop-product-list.shop-list-button-color.label",
      },
    }),
    shop_list_button_text_color: propertyValue({
      type: "color",
      value: { type: "default", value: "" },
      options: {
        label:
          "pages.blocks.shop-product-list.shop-list-button-text-color.label",
      },
    }),
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.shop-product-list.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.shop-product-list.fieldset.appearance.label",
            fields: ["background_color"],
          },
          {
            legend:
              "pages.blocks.shop-product-list.fieldset.category-nav.label",
            fields: [
              "display_burger_menu_categories",
              {
                prop: "burger_background_color",
                visibleWhen: { display_burger_menu_categories: true },
              },
              {
                prop: "burger_icon_color",
                visibleWhen: { display_burger_menu_categories: true },
              },
              "category_list_top",
              "categories_background_color",
              "categories_active_color",
              "categories_inactive_color",
              "secondary_categories_colors.backgroundColor",
              "secondary_categories_colors.activeColor",
              "secondary_categories_colors.inactiveColor",
              "third_categories_colors.backgroundColor",
              "third_categories_colors.activeColor",
              "third_categories_colors.inactiveColor",
            ],
          },
          {
            legend: "pages.blocks.shop-product-list.fieldset.filters.label",
            fields: ["display_allergen_and_tag_filters"],
          },
          {
            legend:
              "pages.blocks.shop-product-list.fieldset.category-titles.label",
            fields: [
              "category_name_color",
              "category_description_color",
              "highlight_category_name_color",
              "highlight_category_background_color",
            ],
          },
          {
            legend:
              "pages.blocks.shop-product-list.fieldset.product-cards.label",
            fields: [
              "product_name_color",
              "product_description_color",
              "product_price_color",
              "product_image_placeholder_color",
              "product_border_color",
              "product_background_color",
            ],
          },
          {
            legend: "pages.blocks.shop-product-list.fieldset.hover.label",
            fields: [
              "product_hover_colors.backgroundColor",
              "product_hover_colors.nameColor",
              "product_hover_colors.descriptionColor",
              "product_hover_colors.priceColor",
            ],
          },
          {
            legend: "pages.blocks.shop-product-list.fieldset.highlight.label",
            fields: [
              "product_highlight_colors.highlight_product_name_color",
              "product_highlight_colors.highlight_product_description_color",
              "product_highlight_colors.highlight_product_price_color",
              "product_highlight_colors.highlight_product_image_placeholder_color",
              "product_highlight_colors.highlight_product_border_color",
              "product_highlight_colors.highlight_product_background_color",
            ],
          },
          {
            legend: "pages.blocks.shop-product-list.fieldset.back-button.label",
            fields: [
              "display_back_shop_list_button_mobile",
              "display_back_shop_list_button_desktop",
              "back_shop_list_button_text",
              "shop_list_url",
              "shop_list_button_color",
              "shop_list_button_text_color",
            ],
          },
        ],
      },
    ],
  }),
);

export function shopProductListBlockDescriptor(): IBlockDescriptor;
export function shopProductListBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function shopProductListBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedShopProductListBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedShopProductListBlockDescriptor,
    shopProductListBlockProperties,
    fieldOrder,
  );
}

export {
  shopProductListBlockBlocDefinition,
  composeShopProductListConfig,
  shopProductListBlockDashboardLayout,
  shopProductListBlockConfig,
  shopProductListBlockResolveConfig,
  shopProductListBlockProperties,
  shopProductListBlockDefinitionSetupScenarios,
};

export type ShopProductListBlockConfig = typeof shopProductListBlockConfig;
